import React, { ComponentType, ReactNode, Fragment, useState } from 'react'
import { ImageLabelingProps } from './ImageLabeling'
import OptionsModal from '../../components/options-wrapper'
import { isImageLabeling, isDropdownAnswer } from '../../utils'
import {
	getImageDropdownKey,
	DropdownAnswer,
	variantTypes,
} from '@mission.io/question-toolkit'
import ImageWrapper from '../../components/image-wrapper/ImageWrapper'
import Footer from '../../components/studentPromptFooter'
import {
	DropdownClozeDropdown,
	DropdownImageLabeling,
	QuestionVariant,
	ClassResponse,
} from '../../model/general'
import ChoiceBox from '../../components/choice-box'
import DropdownReviewResponse from '../../components/dropdown-review-response/DropdownReviewResponse'
import { LabelInformation, GeneralScoreTitle } from '../../assets/styles'
import 'styled-components/macro'
import { styled } from 'styled-components'
import FooterStickyContainer from '../../components/footer-sticky-container/FooterStickyContainer'
import OptionalMarkdown from '../../components/Markdown/OptionalMarkdown'
import { _tempTheme } from '../../utils/tempTheme'

/**
 * A component for a student to view an image labeling question and either answer or review it
 * @returns {JSX.Element}
 */
export default function Student(props: ImageLabelingProps): JSX.Element {
	const {
		question,
		textToSpeech: TextToSpeech,
		review,
		requestId,
		sendMessage,
		studentResponse,
		questionIndex,
		parentRef,
		classResponse,
	} = props

	if (!question || !isImageLabeling(question)) return <Fragment />

	const [error, setError] = useState('')
	const [dropdownIndexSelected, setDropdownIndexSelected] = useState(0)
	const [localStudentAnswer, setLocalStudentAnswer] = useState<DropdownAnswer>({
		type: QuestionVariant.IMAGE_LABELING,
		value: {},
	})

	const handleChangeOption = (index: number) => {
		const dropdown = question.dropdowns[dropdownIndexSelected]
		const choice = dropdown?.choices[index]?._id

		setLocalStudentAnswer(prevState => ({
			...prevState,
			value: {
				...prevState.value,
				// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				[getImageDropdownKey(dropdown)]: String(choice || ''),
			},
		}))

		setError('')
	}

	const handleSubmit = () => {
		if (
			Object.keys(localStudentAnswer.value).length !== question.dropdowns.length
		) {
			setError('Please Complete All Options')
			return
		}

		setError('')
		sendMessage && sendMessage(localStudentAnswer)
	}

	const handleSelectOption = (index: number) => {
		setDropdownIndexSelected(index)
	}
	const instantReview = review === 'INSTANT'
	if (question) {
		if (instantReview || (!review && requestId)) {
			const optionsWrapper = (
				<OptionsModal
					options={question.dropdowns[dropdownIndexSelected]?.choices}
					studentResponse={studentResponse}
					dropdownKey={getImageDropdownKey(
						// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
						question.dropdowns[dropdownIndexSelected]
					)}
					handleClick={handleChangeOption}
					isReview={instantReview}
				/>
			)

			const classResponseScores =
				classResponse &&
				classResponse.scores.type === variantTypes.IMAGE_LABELING &&
				classResponse.scores.optionScores.find(
					i =>
						i.templateResponsekey ===
						// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
						getImageDropdownKey(question.dropdowns[dropdownIndexSelected])
				)

			const optionsStats = classResponse && classResponseScores && (
				<ListOptionsStats
					// @ts-expect-error TS2322 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
					dropdown={question.dropdowns[dropdownIndexSelected]}
					classResponseScores={classResponseScores}
					classResponse={classResponse}
					textToSpeech={TextToSpeech}
					review={review}
				/>
			)

			let studentAnswer: DropdownAnswer | undefined = localStudentAnswer
			if (instantReview) {
				if (!studentResponse || !isDropdownAnswer(studentResponse.answer)) {
					studentAnswer = undefined
				} else {
					studentAnswer = studentResponse?.answer
				}
			}
			return (
				<FooterStickyContainer
					instantReview={instantReview}
					footerContent={
						<>
							{!classResponse && (
								<Footer
									studentAnswer={studentAnswer}
									isReview={instantReview}
									question={question}
									error={error}
									onClickSubmit={handleSubmit}
								/>
							)}
						</>
					}
					topContent={
						<>
							{classResponse ? (
								<GeneralScoreTitle>
									<div className="question-index">{`${props.questionIndex}.`}</div>
									{`${(
										classResponse.generalScore * 100
									).toFixed()}% Answered Correctly`}
								</GeneralScoreTitle>
							) : (
								<LabelInformation>
									<OptionalMarkdown
										className="prose-on-game-light-purple"
										textToSpeech={TextToSpeech}>
										{question.phrase}
									</OptionalMarkdown>
								</LabelInformation>
							)}
							<ImageWrapper
								isReview={instantReview}
								parentRef={parentRef}
								handleSelectOption={handleSelectOption}
								question={question}
								studentAnswer={studentAnswer}
								optionsWrapper={optionsWrapper}
								classResponse={classResponse}
								optionsStats={optionsStats}
								dropdownIndexSelected={dropdownIndexSelected}
							/>
						</>
					}
				/>
			)
		} else if (review === 'OVERVIEW') {
			const optionsWrapper = (
				<OptionsModal
					options={question.dropdowns[dropdownIndexSelected]?.choices}
					studentResponse={studentResponse}
					dropdownKey={getImageDropdownKey(
						// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
						question.dropdowns[dropdownIndexSelected]
					)}
					handleClick={handleChangeOption}
					isReview={true}
				/>
			)
			return (
				<DropdownReviewResponse
					title={question.phrase}
					studentAnswer={studentResponse?.answer}
					index={questionIndex || 0}
					question={question}>
					<ImageWrapper
						parentRef={parentRef}
						handleSelectOption={handleSelectOption}
						studentAnswer={studentResponse?.answer}
						question={question}
						isReview={true}
						optionsWrapper={optionsWrapper}
					/>
				</DropdownReviewResponse>
			)
		}
	}
	return <Fragment />
}

// List of all options with stats for a dropdown selected
export const ListOptionsStats = ({
	dropdown,
	classResponseScores,
	classResponse,
	textToSpeech: TextToSpeech,
	review,
}: {
	dropdown: DropdownImageLabeling | DropdownClozeDropdown
	classResponse: ClassResponse
	classResponseScores: {
		templateResponsekey: string
		choices: Array<{ _id: string; score: number }>
	}
	textToSpeech?: ComponentType<{
		children: ReactNode
		onStartSpeech?: (event: TouchEvent | MouseEvent) => void
	}>
	review?: 'OVERVIEW' | 'INSTANT'
}): JSX.Element => {
	const instantReview = review === 'INSTANT'

	return (
		<ListOptionsStatsStyled role="list">
			{dropdown.choices.map((choice, i) => {
				const optionScore =
					classResponseScores &&
					classResponseScores.choices.find(o => o._id === String(choice._id))
						?.score
				return (
					<ChoiceBox
						key={String(choice._id)}
						isReview={instantReview}
						choice={choice}
						textToSpeech={TextToSpeech}
						classResponse={classResponse}
						optionScore={optionScore || 0}
						isRow
						showOptionIndicator={false}
						index={i}
					/>
				)
			})}
		</ListOptionsStatsStyled>
	)
}

const ListOptionsStatsStyled = styled.div`
	border: ${({ theme }) =>
		`${_tempTheme.spacing1} solid ${theme.lightSuccess}`};
	padding: ${_tempTheme.spacing2} ${_tempTheme.spacing4};
	border-radius: ${_tempTheme.spacing2};
	background: ${({ theme }) => theme.lightPrimaryColor};
`
